import { isOnline } from '../../shell/connectivity/connectivityInfra';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { getAppSettingSafe, persistAppSettingSafe } from '../../sync/core/persistAppSetting';
import {
  PURCHASES_LAST_PULL_AT_KEY,
  recordSyncPullTimestamp,
} from '../../sync/core/syncTimestamps';
import { applyPurchasesPullLocal } from '../infra/purchasesPullLocal';
import { pullBranchPurchasesRemote } from '../infra/purchasesPullRemote';
import { setWebPurchasesSnapshotCache } from '../infra/webPurchasesSnapshotCache';

const PULL_DEBOUNCE_MS = 30_000;
const PULL_TIMEOUT_MS = 12_000;

let lastPullAttemptAt = 0;
let pullInFlight: Promise<{ pulled: boolean; error?: string }> | null = null;
let pullGeneration = 0;

async function runPurchasesPull(
  branchId: string,
  generation: number,
): Promise<{ pulled: boolean; error?: string }> {
  try {
    const sinceIso = null;
    const payload = await Promise.race([
      pullBranchPurchasesRemote(branchId, sinceIso),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('purchases pull remote timeout')), PULL_TIMEOUT_MS),
      ),
    ]);

    if (generation !== pullGeneration) {
      return { pulled: false };
    }

    setWebPurchasesSnapshotCache(payload);
            const nowIso = new Date().toISOString();
            recordSyncPullTimestamp(PURCHASES_LAST_PULL_AT_KEY, nowIso);
            return { pulled: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Error al sincronizar compras';
    console.warn('[pullPurchasesWhenNeeded]', message);
    return { pulled: false, error: message };
  }
}

export async function pullPurchasesWhenNeeded(
  branchId: string,
  options?: { force?: boolean; requireFresh?: boolean },
): Promise<{ pulled: boolean; error?: string }> {
  if (!branchId) return { pulled: false };

  const online = await isOnline();
  if (!online) return { pulled: false };

  const now = Date.now();
  if (!options?.force && now - lastPullAttemptAt < PULL_DEBOUNCE_MS) {
    return { pulled: false };
  }

  if (pullInFlight) {
    if (!options?.requireFresh) return pullInFlight;
    // El pull en vuelo pudo fetchear antes del write que dispara este force:
    // esperarlo y arrancar uno fresco. Si ya arrancó otro (post-write), coalesce.
    await pullInFlight.catch(() => undefined);
    if (pullInFlight) return pullInFlight;
  }

  lastPullAttemptAt = now;
  const generation = ++pullGeneration;

  pullInFlight = runPurchasesPull(branchId, generation).finally(() => {
    if (pullInFlight) {
      pullInFlight = null;
    }
  });

  return pullInFlight;
}

// ─── E2E test-only endpoint ──────────────────────────────────────────────────
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__e2eForcePurchasesPull = async (): Promise<{
    pulled: boolean;
    error?: string;
  }> => {
    const branchId = useSessionStore.getState().currentBranchId;
    if (!branchId) return { pulled: false, error: 'No currentBranchId' };
    await persistAppSettingSafe(PURCHASES_LAST_PULL_AT_KEY, '1970-01-01T00:00:00.000Z');
    lastPullAttemptAt = 0;
    return pullPurchasesWhenNeeded(branchId, { force: true });
  };
}
